import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, FileUp, Loader2, Upload, X, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, runExtract } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { RentRollEditor, toPayload } from "./RentRollEditor";

export const DOC_TYPES = ["Rent roll", "Appraisal", "Financials / T-12", "Term sheet", "Tax return", "Offering memorandum", "Other"];
export const ACCEPT = ".pdf,.xlsx,.csv,.docx,.png,.jpg,.jpeg,.webp,.txt";
const CLASSES = [["office", "Office"], ["retail", "Retail"], ["multifamily", "Multifamily"], ["industrial", "Industrial"], ["hospitality", "Hospitality"]];
const inp = "w-full text-sm border border-slate-300 rounded-lg px-3 py-2 bg-white outline-none focus:border-sky-500";

export function guessType(n) {
  n = n.toLowerCase();
  if (/1065|1120|8825|tax.?return|corporate.?return|schedule.?e/.test(n)) return "Tax return";
  if (/offering|memorandum|(^|[\s_\-(])om([\s_\-.)(]|$)/.test(n)) return "Offering memorandum";
  if (/rent|roll/.test(n)) return "Rent roll";
  if (/apprais|valuation/.test(n)) return "Appraisal";
  if (/t-?12|operat|financ|p&l|income/.test(n)) return "Financials / T-12";
  if (/term|loi|commitment/.test(n)) return "Term sheet";
  return "Other";
}

export function FilePicker({ files, setFiles, disabled, id = "new-loan" }) {
  const ref = useRef(null);
  const [over, setOver] = useState(false);
  const add = (list) => setFiles((f) => [...f, ...[...list].map((file) => ({ file, doc_type: guessType(file.name), state: "queued" }))]);
  return (
    <div className="space-y-2">
      <div data-testid={`${id}-dropzone`} onClick={() => !disabled && ref.current?.click()} onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); !disabled && add(e.dataTransfer.files); }}
        className={`border-2 border-dashed rounded-xl px-4 py-6 text-center cursor-pointer transition-colors ${over ? "border-sky-500 bg-sky-50" : "border-slate-300 hover:border-sky-400 bg-slate-50/60"}`}>
        <Upload className="w-5 h-5 mx-auto text-sky-600" />
        <div className="text-sm text-slate-800 mt-2 font-medium">Drop the rent roll, appraisal, T-12 / financials and term sheet</div>
        <div className="text-[11px] text-slate-500 mt-0.5">PDF (text or scanned), Excel, CSV, Word or images · up to 25 MB each</div>
        <input ref={ref} type="file" multiple accept={ACCEPT} className="hidden" data-testid={`${id}-file-input`} onChange={(e) => { add(e.target.files); e.target.value = ""; }} />
      </div>
      {files.map((f, i) => (
        <div key={i} className="flex items-center gap-2 text-xs border border-slate-200 rounded-lg px-3 py-2" data-testid={`${id}-file-${i}`}>
          {f.state === "uploading" ? <Loader2 className="w-3.5 h-3.5 animate-spin text-sky-600" /> : f.state === "done" ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> : f.state === "error" ? <XCircle className="w-3.5 h-3.5 text-red-600" /> : <FileUp className="w-3.5 h-3.5 text-slate-400" />}
          <span className="flex-1 truncate text-slate-800" title={f.error || f.file.name}>{f.file.name}{f.error && <span className="text-red-600"> · {f.error}</span>}</span>
          <span className="num text-[10px] text-slate-400">{f.file.size < 1048576 ? `${Math.max(1, Math.round(f.file.size / 1024))} KB` : `${(f.file.size / 1048576).toFixed(1)} MB`}</span>
          <select data-testid={`${id}-file-type-${i}`} disabled={disabled} value={f.doc_type} onChange={(e) => setFiles((l) => l.map((x, j) => (j === i ? { ...x, doc_type: e.target.value } : x)))} className="text-[11px] border border-slate-300 rounded px-1.5 py-1 bg-white">
            {DOC_TYPES.map((t) => <option key={t}>{t}</option>)}
          </select>
          {!disabled && <button data-testid={`${id}-file-remove-${i}`} onClick={() => setFiles((l) => l.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>}
        </div>
      ))}
    </div>
  );
}

export async function waitForDocs(taskId, onLeft) {
  for (let i = 0; i < 225; i++) {
    const { data } = await api.get(`/tasks/${taskId}`);
    const left = data.documents.filter((d) => d.status === "processing").length;
    if (!left) return true;
    onLeft(left);
    await new Promise((r) => setTimeout(r, 4000));
  }
  return false;
}

export async function uploadAll(taskId, files, setFiles) {
  let ok = 0;
  for (let i = 0; i < files.length; i++) {
    if (files[i].state === "done") continue;
    setFiles((l) => l.map((x, j) => (j === i ? { ...x, state: "uploading" } : x)));
    const fd = new FormData();
    fd.append("file", files[i].file);
    fd.append("doc_type", files[i].doc_type);
    try { await api.post(`/tasks/${taskId}/documents/upload`, fd); ok++; setFiles((l) => l.map((x, j) => (j === i ? { ...x, state: "done" } : x))); }
    catch (e) { setFiles((l) => l.map((x, j) => (j === i ? { ...x, state: "error", error: e.response?.data?.detail || "Upload failed" } : x))); }
  }
  return ok;
}

export function NewLoanDialog({ open, onOpenChange }) {
  const [f, setF] = useState({ name: "", property_class: "office", requested_amount: "", purpose: "Acquisition", location: "", borrower: "" });
  const [files, setFiles] = useState([]);
  const [stage, setStage] = useState(null);
  const [taskId, setTaskId] = useState(null);
  const [units, setUnits] = useState([]);
  const [showUnits, setShowUnits] = useState(false);
  const { refreshTasks } = useApp();
  const nav = useNavigate();
  const amt = Number(String(f.requested_amount).replace(/[$,\s]/g, ""));
  const valid = f.name.trim() && amt > 0 && files.length > 0;
  const run = async () => {
    try {
      let t = { id: taskId };
      if (!taskId) {
        setStage("Creating loan file…");
        t = (await api.post("/lending/loans", { ...f, requested_amount: amt })).data;
        setTaskId(t.id);
        const rows = toPayload(units);
        if (rows.length) {
          setStage("Saving unit rent roll…");
          try { await api.put(`/tasks/${t.id}/rent-roll`, { units: rows, note: "Entered at intake" }); } catch (e) { toast.error(`Rent roll not saved: ${errMsg(e) || "check the rows"} - fix it on the Rent roll tab`); }
        }
      }
      setStage("Uploading & reading documents…");
      const ok = await uploadAll(t.id, files, setFiles);
      if (!ok) { setStage(null); await refreshTasks(); return toast.error("No documents could be read - fix the files and upload them from the Evidence tab"); }
      if (!(await waitForDocs(t.id, (n) => setStage(`Reading scanned pages with vision OCR… ${n} document${n > 1 ? "s" : ""} still in progress`)))) {
        setStage(null); await refreshTasks(); return toast.error("Scanned pages are taking unusually long - run extraction from the Evidence tab when they finish");
      }
      setStage("AI is extracting figures with page citations…");
      await runExtract(t.id);
      await refreshTasks();
      toast.success("Analysis ready - review flagged figures on the Evidence tab");
      onOpenChange(false); setStage(null); setFiles([]); setTaskId(null); setUnits([]); setShowUnits(false);
      nav(`/task/${t.id}?tab=evidence`);
    } catch (e) { setStage(null); if (e.response?.status !== 403) toast.error(errMsg(e) || "Could not create the loan"); }
  };
  const field = (k, label, props = {}) => (
    <label className="block"><span className="text-[11px] font-semibold text-slate-600">{label}</span>
      <input data-testid={`new-loan-${k}`} disabled={!!stage} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className={inp} {...props} /></label>
  );
  return (
    <Dialog open={open} onOpenChange={(o) => { if (stage) return; if (!o && taskId) { setTaskId(null); setFiles([]); refreshTasks(); } onOpenChange(o); }}>
      <DialogContent className="max-w-4xl bg-white max-h-[90vh] overflow-y-auto" data-testid="new-loan-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">New loan from documents</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500 -mt-2">Only your documents are used. Every figure is cited to its document and page, and anything missing or uncertain is flagged for you before the engine recommends.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {field("name", "Loan / property name *", { placeholder: "e.g. Riverside Lofts", disabled: !!stage || !!taskId })}
          <label className="block"><span className="text-[11px] font-semibold text-slate-600">Property type *</span>
            <select data-testid="new-loan-property_class" disabled={!!stage} value={f.property_class} onChange={(e) => setF({ ...f, property_class: e.target.value })} className={inp}>{CLASSES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></label>
          {field("requested_amount", "Requested amount (USD) *", { placeholder: "24,000,000", inputMode: "numeric" })}
          <label className="block"><span className="text-[11px] font-semibold text-slate-600">Purpose</span>
            <select data-testid="new-loan-purpose" disabled={!!stage} value={f.purpose} onChange={(e) => setF({ ...f, purpose: e.target.value })} className={inp}>{["Acquisition", "Refinance", "Construction", "Cash-out refinance"].map((p) => <option key={p}>{p}</option>)}</select></label>
          {field("location", "Location", { placeholder: "City, State" })}
          {field("borrower", "Borrower", { placeholder: "Borrowing entity" })}
        </div>
        <FilePicker files={files} setFiles={setFiles} disabled={!!stage} />
        <div className="rounded-xl border border-slate-200 p-3 min-w-0" data-testid="new-loan-units-section">
          <button type="button" data-testid="new-loan-units-toggle" onClick={() => setShowUnits(!showUnits)} className="text-xs font-semibold text-slate-700 flex w-full justify-between">
            <span>Rents by unit (optional){units.length ? ` · ${units.length} entered` : ""}</span><span className="text-sky-700">{showUnits ? "Hide" : "Enter or paste units"}</span></button>
          {showUnits && <div className="mt-3"><RentRollEditor units={units} setUnits={setUnits} disabled={!!stage || !!taskId} id="new-loan-units" /></div>}
          <div className="text-[10px] text-slate-500 mt-1">Shown next to the rent roll figures extracted from your documents; it doesn't change the underwriting numbers.</div>
        </div>
        <div className="text-[11px] text-slate-500" data-testid="sample-doc-links">No files handy? Sample set: {["Riverside_Lofts_Rent_Roll.pdf", "Riverside_Lofts_T12.xlsx", "Riverside_Lofts_Term_Sheet.docx", "Riverside_Lofts_Appraisal_scanned.pdf"].map((n, i) => <a key={n} href={`/sample-docs/${n}`} download className="text-sky-700 hover:underline">{i ? " · " : ""}{n.replace("Riverside_Lofts_", "").replace(/_/g, " ")}</a>)}</div>
        <div className="flex items-center justify-between gap-3 pt-1">
          <span className="text-xs text-sky-700 flex items-center gap-2" data-testid="new-loan-stage">{stage && <><Loader2 className="w-3.5 h-3.5 animate-spin" />{stage}</>}</span>
          <button data-testid="new-loan-submit" disabled={!valid || !!stage} onClick={run} className="btn-primary disabled:opacity-40">{stage ? "Working…" : taskId ? "Retry upload & analysis" : "Create & analyse"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
